import { Link } from 'react-router-dom';
import { Users, AlertTriangle, TrendingUp, ChevronRight } from 'lucide-react';

/**
 * ClientRiskTable Component
 * Displays top clients sorted by risk score
 */
export default function ClientRiskTable({ data, loading = false }) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { clientes = [], resumen = {}, moneda = 'CRC' } = data;

  // Take top 5 clients (already sorted by score, lowest first = highest risk)
  const topClients = clientes.slice(0, 5);

  // Format currency based on predominant currency
  const formatCurrency = (value) => {
    if (moneda === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Score color and badge
  const getScoreStyle = (score, nivel) => {
    if (nivel === 'bajo') {
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        ring: 'ring-emerald-500'
      };
    }
    if (nivel === 'medio') {
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        ring: 'ring-amber-500'
      };
    }
    return {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      ring: 'ring-red-500'
    };
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200">
              <Users className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Analisis de Riesgo - Clientes</h2>
              <p className="text-xs text-gray-500">Top 5 clientes por nivel de riesgo</p>
            </div>
          </div>

          {/* Summary badges */}
          <div className="flex items-center gap-2">
            {resumen.altoRiesgo > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                {resumen.altoRiesgo} alto
              </span>
            )}
            {resumen.riesgoMedio > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                {resumen.riesgoMedio} medio
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      {topClients.length === 0 ? (
        <div className="p-8 text-center">
          <div className="h-12 w-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="h-6 w-6 text-emerald-600" />
          </div>
          <p className="text-sm font-medium text-gray-900">Sin clientes de riesgo</p>
          <p className="text-xs text-gray-500">Todos tus clientes estan al dia</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {topClients.map((client, index) => {
            const style = getScoreStyle(client.score, client.nivel);

            return (
              <div
                key={client.id}
                className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Rank */}
                  <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                    {index + 1}
                  </div>

                  {/* Client info */}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {client.nombre}
                    </p>
                    <p className="text-xs text-gray-500">
                      {client.comportamiento} • {client.facturasVencidas} vencidas
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Balance */}
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(client.saldoPendiente)}
                    </p>
                    <p className="text-xs text-gray-500">pendiente</p>
                  </div>

                  {/* Score badge */}
                  <div className={`px-2.5 py-1 rounded-lg border ${style.bg} ${style.border}`}>
                    <span className={`text-sm font-bold ${style.text}`}>
                      {client.score}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {topClients.length > 0 && (
        <div className="p-3 bg-gray-50 border-t border-gray-100 rounded-b-xl">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Total pendiente: <span className="font-medium text-gray-700">{formatCurrency(resumen.totalPendiente)}</span>
            </p>
            <Link
              to="/cxc/pendientes"
              className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              Ver CxC completo
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
